import React, { useState } from 'react';
import DashboardLayout from '../../components/includes/DashboardLayout';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "../../components/ui/alert-dialog";
import {
  Search,
  Plus,
  Users,
  Briefcase,
  MoreHorizontal,
  Mail,
  Phone,
  Eye,
  Edit, // Changed from Edit2 to Edit based on previous file usage, or just Edit
  Trash2,
  CheckCircle,
  Loader2,
} from "lucide-react";

import { useTeachers } from '../../contexts/TeachersContext';
import { Teacher } from '../../api/teachers';
import AddProfessor from './AddProfessor';
import ViewProfessor from './ViewProfessor';
import { useToast } from "../../hooks/use-toast";

const Professors: React.FC = () => {
  const { teachers, isLoading, deleteTeacher } = useTeachers();
  const { toast } = useToast();

  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
  const [teacherToDelete, setTeacherToDelete] = useState<Teacher | null>(null);

  const filteredTeachers = teachers.filter(teacher => {
    const searchLower = searchTerm.toLowerCase();
    return (
      teacher.firstName.toLowerCase().includes(searchLower) ||
      teacher.lastName.toLowerCase().includes(searchLower) ||
      teacher.email.toLowerCase().includes(searchLower) ||
      (teacher.specialization && teacher.specialization.toLowerCase().includes(searchLower))
    );
  });

  const handleDeleteTeacher = async () => {
    if (!teacherToDelete) return;

    try {
      await deleteTeacher(teacherToDelete.id);
      toast({
        title: "Succès",
        description: "Le professeur a été supprimé avec succès",
      });
      setTeacherToDelete(null);
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message || "Une erreur est survenue lors de la suppression",
        variant: "destructive",
      });
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  };

  const stats = [
    {
      title: 'Total Professeurs',
      value: teachers.length.toString(),
      icon: Users,
      color: 'text-blue-600',
      bg: 'bg-blue-100 dark:bg-blue-900/20',
    },
    {
      title: 'Professeurs Actifs',
      value: teachers.length.toString(), // Mock logic for now as status isn't checking active/inactive
      icon: CheckCircle,
      color: 'text-green-600',
      bg: 'bg-green-100 dark:bg-green-900/20',
    },
    {
      title: 'Spécialisations',
      value: new Set(teachers.map(t => t.specialization).filter(Boolean)).size.toString(),
      icon: Briefcase,
      color: 'text-purple-600',
      bg: 'bg-purple-100 dark:bg-purple-900/20',
    },
  ];

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
              Gestion des Professeurs
            </h1>
            <p className="text-muted-foreground mt-1">
              Gérez le corps professoral de l'ISTMECH
            </p>
          </div>
          <Button onClick={() => {
            setSelectedTeacher(null);
            setShowAddModal(true);
          }}>
            <Plus className="mr-2 h-4 w-4" /> Nouveau Professeur
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {stats.map((stat, index) => (
            <Card key={index}>
              <CardContent className="p-6 flex items-center justify-between space-x-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {stat.title}
                  </p>
                  <div className="flex items-baseline gap-2">
                    <h3 className="text-2xl font-bold">{stat.value}</h3>
                  </div>
                </div>
                <div className={`p-3 rounded-full ${stat.bg} ${stat.color}`}>
                  <stat.icon className="h-6 w-6" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Content */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <CardTitle>Liste des professeurs</CardTitle>
              <div className="relative w-full md:w-[300px]">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher un professeur..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <CardDescription>
              Gérez les informations et les spécialisations des professeurs.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Professeur</TableHead>
                    <TableHead>Spécialisation</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Cours</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTeachers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-24 text-center">
                        Aucun professeur trouvé.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTeachers.map((teacher) => (
                      <TableRow key={teacher.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9">
                              <AvatarImage src={`https://api.dicebear.com/7.x/initials/svg?seed=${teacher.firstName} ${teacher.lastName}`} />
                              <AvatarFallback>{getInitials(teacher.firstName, teacher.lastName)}</AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col">
                              <span className="font-semibold">{teacher.firstName} {teacher.lastName}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {teacher.specialization || "-"}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Mail className="h-3 w-3" /> {teacher.email}
                            </div>
                            <div className="flex items-center gap-1">
                              <Phone className="h-3 w-3" /> {teacher.phone}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {teacher.courses && teacher.courses.length > 0 ? (
                              <>
                                {teacher.courses.slice(0, 2).map((course, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {course.code}
                                  </Badge>
                                ))}
                                {teacher.courses.length > 2 && (
                                  <Badge variant="outline" className="text-xs">+{teacher.courses.length - 2}</Badge>
                                )}
                              </>
                            ) : (
                              <span className="text-xs text-muted-foreground">Aucun cours</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
                                <span className="sr-only">Ouvrir menu</span>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuItem
                                className="cursor-pointer"
                                onClick={() => {
                                  setSelectedTeacher(teacher);
                                  setShowViewModal(true);
                                }}
                              >
                                <Eye className="mr-2 h-4 w-4" /> Voir profil
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="cursor-pointer"
                                onClick={() => {
                                  setSelectedTeacher(teacher);
                                  setShowAddModal(true);
                                }}
                              >
                                <Edit className="mr-2 h-4 w-4" /> Modifier
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="cursor-pointer text-red-600 focus:text-red-600"
                                onClick={() => setTeacherToDelete(teacher)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" /> Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            <div className="text-xs text-muted-foreground mt-4">
              Affichage de <strong>{filteredTeachers.length}</strong> sur <strong>{teachers.length}</strong> professeurs
            </div>
          </CardContent>
        </Card>
      </div>

      <AddProfessor
        open={showAddModal}
        onOpenChange={setShowAddModal}
        professorToEdit={selectedTeacher}
      />

      <ViewProfessor
        open={showViewModal}
        onOpenChange={setShowViewModal}
        professor={selectedTeacher}
      />

      <AlertDialog open={!!teacherToDelete} onOpenChange={(open) => !open && setTeacherToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Êtes-vous sûr ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action supprimera définitivement le professeur
              <strong> {teacherToDelete?.firstName} {teacherToDelete?.lastName}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTeacher}
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default Professors;
